import {
    Alert,
    Button,
    Group,
    Loader,
    Paper,
    Select,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import {useForm} from '@mantine/form'
import {Link} from '@tanstack/react-router'
import {useEffect, useState} from 'react'

import {
    AssignmentStrip,
    PLANNED_ASSIGNMENT_STRIP_LEGEND_LABELS,
} from 'src/assignments/assignment-strip'
import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'
import {EquipmentDeploymentHistorySection} from 'src/equipment-deployment/equipment-deployment-history-section'
import {EquipmentDeploymentStatusSection} from 'src/equipment-deployment/equipment-deployment-status-section'
import {useEquipmentLocationEvents} from 'src/equipment-deployment/use-equipment-location-events'
import {ConfirmDialog} from 'src/ui/confirm-dialog'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import {OutOfServicePanel} from './out-of-service-panel'
import {useRentalAssetAssignments} from './use-rental-asset-assignments'
import {useRentalAssetTypes} from './use-rental-asset-types'
import {
    useArchiveRentalAsset,
    useRentalAsset,
    useUnarchiveRentalAsset,
    useUpdateRentalAsset,
} from './use-rental-assets'

export type RentalAssetDetailsPageState =
    | 'loading'
    | 'error'
    | 'notFound'
    | 'reading'
    | 'editing'
    | 'saving'

type RentalAssetFormValues = {
    name: string
    rentalAssetTypeId: string | null
}

type RentalAssetDetailsPageProps = {
    assetId: string
}

function validateRentalAssetForm(values: RentalAssetFormValues) {
    const errors: Partial<Record<keyof RentalAssetFormValues, string>> = {}
    if (!values.name.trim()) {
        errors.name = 'Asset name is required'
    }
    if (!values.rentalAssetTypeId) {
        errors.rentalAssetTypeId = 'Asset type is required'
    }
    return errors
}

function isNotFoundError(error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to load rental asset'
    return message.toLowerCase().includes('not found')
}

export function RentalAssetDetailsPage({assetId}: RentalAssetDetailsPageProps) {
    const user = useCurrentUser()
    const {data: asset, isLoading, isError, error, refetch} = useRentalAsset(assetId)
    const {
        data: assignmentList,
        isLoading: assignmentsLoading,
        isError: assignmentsError,
        error: assignmentsLoadError,
        refetch: refetchAssignments,
        referenceDate,
    } = useRentalAssetAssignments(assetId, {enabled: Boolean(asset)})
    const {
        data: deploymentData,
        isLoading: deploymentLoading,
        isError: deploymentError,
        error: deploymentLoadError,
        refetch: refetchDeployment,
    } = useEquipmentLocationEvents(assetId, {enabled: Boolean(asset)})
    const {data: rentalAssetTypes = []} = useRentalAssetTypes()
    const updateRentalAsset = useUpdateRentalAsset()
    const archiveRentalAsset = useArchiveRentalAsset()
    const unarchiveRentalAsset = useUnarchiveRentalAsset()
    const [pageState, setPageState] = useState<RentalAssetDetailsPageState>('loading')
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [archiveModalOpen, setArchiveModalOpen] = useState(false)

    const form = useForm<RentalAssetFormValues>({
        initialValues: {
            name: '',
            rentalAssetTypeId: null,
        },
        validate: validateRentalAssetForm,
    })

    useEffect(() => {
        if (isLoading) {
            setPageState('loading')
            return
        }

        if (isError) {
            if (isNotFoundError(error)) {
                setPageState('notFound')
                return
            }

            setErrorMessage(error instanceof Error ? error.message : 'Failed to load rental asset')
            setPageState('error')
            return
        }

        if (asset) {
            form.setValues({
                name: asset.name,
                rentalAssetTypeId: asset.rentalAssetTypeId,
            })
            if (pageState === 'loading' || pageState === 'error') {
                setPageState('reading')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- form identity is stable; omit to avoid reload on edit
    }, [asset, isLoading, isError, error])

    function handleEdit() {
        if (!asset) {
            return
        }

        form.setValues({
            name: asset.name,
            rentalAssetTypeId: asset.rentalAssetTypeId,
        })
        setPageState('editing')
    }

    function handleCancel() {
        if (!asset) {
            return
        }

        form.setValues({
            name: asset.name,
            rentalAssetTypeId: asset.rentalAssetTypeId,
        })
        setPageState('reading')
    }

    async function handleSave() {
        if (!asset || !form.values.rentalAssetTypeId) {
            return
        }

        const validation = form.validate()
        if (validation.hasErrors) {
            return
        }

        setPageState('saving')
        setErrorMessage(null)

        try {
            await updateRentalAsset.mutateAsync({
                id: asset.id,
                input: {
                    name: form.values.name.trim(),
                    rentalAssetTypeId: form.values.rentalAssetTypeId,
                },
            })
            setPageState('reading')
        } catch (saveError) {
            const message =
                saveError instanceof Error ? saveError.message : 'Failed to save rental asset'
            setErrorMessage(message)
            setPageState('editing')
        }
    }

    async function handleArchiveConfirm() {
        if (!asset) {
            return
        }
        try {
            await archiveRentalAsset.mutateAsync(asset.id)
            setArchiveModalOpen(false)
        } catch (archiveError) {
            setArchiveModalOpen(false)
            setErrorMessage(
                archiveError instanceof Error
                    ? archiveError.message
                    : 'Failed to archive rental asset',
            )
        }
    }

    async function handleUnarchive() {
        if (!asset) {
            return
        }
        try {
            await unarchiveRentalAsset.mutateAsync(asset.id)
        } catch (unarchiveError) {
            setErrorMessage(
                unarchiveError instanceof Error
                    ? unarchiveError.message
                    : 'Failed to unarchive rental asset',
            )
        }
    }

    if (pageState === 'loading') {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        )
    }

    if (pageState === 'notFound') {
        return (
            <Stack gap="md">
                <Title order={1}>Rental asset not found</Title>
                <Text c="dimmed">This asset may have been removed or the link is invalid.</Text>
                <DetailPageBackLink to="/rental-assets" listLabel="rental assets" />
            </Stack>
        )
    }

    if (pageState === 'error' || !asset) {
        return (
            <Stack gap="md">
                <Title order={1}>Rental asset details</Title>
                <Alert color="red" title="Could not load rental asset">
                    {errorMessage ?? 'Something went wrong.'}
                </Alert>
                <Button onClick={() => void refetch()} variant="light">
                    Retry
                </Button>
            </Stack>
        )
    }

    const isEditing = pageState === 'editing' || pageState === 'saving'
    const typeOptions = rentalAssetTypes.map((type) => ({
        value: type.id,
        label: type.name,
    }))

    return (
        <Stack gap="md">
            <Stack gap={4}>
                <DetailPageBackLink to="/rental-assets" listLabel="rental assets" />

                <Group justify="space-between" align="flex-start">
                    <Title order={1}>{asset.name}</Title>

                    {pageState === 'reading' && isAdmin(user) && (
                        <Group gap="sm">
                            <Button onClick={handleEdit}>Edit</Button>
                            {asset.isUniquelyIdentified &&
                                (asset.archivedAt == null ? (
                                    <Button
                                        variant="subtle"
                                        color="red"
                                        onClick={() => setArchiveModalOpen(true)}
                                    >
                                        Archive
                                    </Button>
                                ) : (
                                    <Button variant="subtle" onClick={() => void handleUnarchive()}>
                                        Unarchive
                                    </Button>
                                ))}
                        </Group>
                    )}

                    {isEditing && (
                        <Group gap="sm">
                            <Button
                                variant="default"
                                onClick={handleCancel}
                                disabled={pageState === 'saving'}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => void handleSave()}
                                loading={pageState === 'saving'}
                            >
                                Save
                            </Button>
                        </Group>
                    )}
                </Group>
            </Stack>

            {pageState === 'reading' && asset.isUniquelyIdentified && asset.archivedAt == null ? (
                <OutOfServicePanel rentalAssetId={asset.id} yardId={asset.dispatchYardId} />
            ) : null}

            {errorMessage && isEditing && (
                <Alert color="red" title="Could not save rental asset">
                    {errorMessage}
                </Alert>
            )}

            {pageState === 'reading' ? (
                <Paper withBorder p="md" radius="md">
                    <Stack gap="sm">
                        <div>
                            <Text size="sm" c="dimmed">
                                Name
                            </Text>
                            <Text>{asset.name}</Text>
                        </div>
                        <div>
                            <Text size="sm" c="dimmed">
                                Type
                            </Text>
                            <Text
                                component={Link}
                                to="/rental-assets/types/$typeId"
                                params={{typeId: asset.rentalAssetTypeId}}
                            >
                                {asset.rentalAssetTypeName ?? '—'}
                            </Text>
                        </div>
                    </Stack>
                </Paper>
            ) : (
                <Paper withBorder p="md" radius="md">
                    <Stack gap="md">
                        <TextInput label="Name" withAsterisk {...form.getInputProps('name')} />
                        <Select
                            label="Type"
                            withAsterisk
                            data={typeOptions}
                            {...form.getInputProps('rentalAssetTypeId')}
                        />
                    </Stack>
                </Paper>
            )}

            {deploymentLoading && (
                <Group justify="center" py="md">
                    <Loader size="sm" />
                </Group>
            )}

            {deploymentError && (
                <Alert color="red" title="Could not load deployment history">
                    {deploymentLoadError instanceof Error
                        ? deploymentLoadError.message
                        : 'Something went wrong.'}
                    <Button
                        onClick={() => void refetchDeployment()}
                        variant="light"
                        size="compact-sm"
                        mt="sm"
                    >
                        Retry
                    </Button>
                </Alert>
            )}

            {deploymentData && !deploymentLoading && !deploymentError && (
                <>
                    <EquipmentDeploymentStatusSection
                        currentStatus={deploymentData.currentStatus}
                    />
                    <EquipmentDeploymentHistorySection events={deploymentData.events} />
                </>
            )}

            {assignmentsLoading && (
                <Group justify="center" py="md">
                    <Loader size="sm" />
                </Group>
            )}

            {assignmentsError && (
                <Alert color="red" title="Could not load assignments">
                    {assignmentsLoadError instanceof Error
                        ? assignmentsLoadError.message
                        : 'Something went wrong.'}
                    <Button
                        onClick={() => void refetchAssignments()}
                        variant="light"
                        size="compact-sm"
                        mt="sm"
                    >
                        Retry
                    </Button>
                </Alert>
            )}

            {assignmentList && !assignmentsLoading && !assignmentsError && (
                <AssignmentStrip
                    assignments={assignmentList.assignments}
                    referenceDate={referenceDate}
                    presentation="schedule"
                    title="Planned assignments"
                    description="Job reservations on the calendar — not field deployment."
                    legendLabels={PLANNED_ASSIGNMENT_STRIP_LEGEND_LABELS}
                    showLegend
                />
            )}

            <ConfirmDialog
                opened={archiveModalOpen}
                title="Archive rental asset"
                message={`Are you sure you want to archive ${asset.name ?? 'this asset'}? Archived assets cannot be assigned to new jobs.`}
                confirmLabel="Archive"
                loading={archiveRentalAsset.isPending}
                onConfirm={() => void handleArchiveConfirm()}
                onCancel={() => setArchiveModalOpen(false)}
            />
        </Stack>
    )
}
